import { useState, useCallback } from 'react';
import {
  GridRowModel,
  GridCellEditCommitParams,
} from '@mui/x-data-grid-premium';
import Actions from '../../../store/Actions';
import { getRowId } from '../utils';
import {
  OTCRow,
  UseOTCOperationsProps,
  UseOTCOperationsReturn,
  DAYS_SINCE_ILLIO_UPDATE,
} from '../types';
import { OTCUpdateRequest } from '../../../types/generatedTypes';
import { useCellUndoRedo } from '../../../components/UndoRedo/useCellUndoRedo';
import { COLUMN_FIELDS, DATE_FORMATS } from '../OTCConstants';
import {
  parseNumberWithCommas,
  sanitizeStringValue,
} from '../../../utils/numberFormatter';
import { showSnackbar } from '../../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../../context/Snackbar/Types';

export const useOTCOperations = ({
  otcData,
  originalData,
  setOTCData,
  columns,
  checkDifferenceWithIllioForRow,
  calculatePnL,
  clearUpdatedCellsForRows,
  setOriginalData,
  fetchOTCData,
  selectedRows,
  setSelectedRows,
}: UseOTCOperationsProps): UseOTCOperationsReturn => {
  // Helper function to check if values are equal (handles null, empty strings, and numbers with commas)
  const areValuesEqual = useCallback(
    (value1: unknown, value2: unknown): boolean => {
      // Case: both values are null, undefined, or empty string
      const isValue1Empty =
        value1 === null || value1 === undefined || value1 === '';
      const isValue2Empty =
        value2 === null || value2 === undefined || value2 === '';

      if (isValue1Empty && isValue2Empty) {
        return true; // Both are empty, so they're equal
      }

      // If one is empty and the other isn't, they're not equal
      if (isValue1Empty || isValue2Empty) {
        return false;
      }

      // Handle number comparison with comma parsing
      const str1 = String(value1);
      const str2 = String(value2);

      // Try to parse both values as numbers (handling commas)
      const parsed1 = parseNumberWithCommas(str1);
      const parsed2 = parseNumberWithCommas(str2);

      // If both are valid numbers, compare them numerically
      if (!isNaN(parsed1) && !isNaN(parsed2)) {
        return parsed1 === parsed2;
      }

      // If one is a number and the other isn't, they're not equal
      if (!isNaN(parsed1) || !isNaN(parsed2)) {
        return false;
      }

      // Default string comparison
      return str1 === str2;
    },
    []
  );

  // Function to update the server with the changed row data
  const updateServerWithRow = useCallback(
    async (updatedRow: GridRowModel<OTCRow>) => {
      // Exclude PNL and days since update from server data
      const {
        [COLUMN_FIELDS.PNL]: pnl, // eslint-disable-line @typescript-eslint/no-unused-vars
        [DAYS_SINCE_ILLIO_UPDATE]: daysSince, // eslint-disable-line @typescript-eslint/no-unused-vars
        ...rowData
      } = updatedRow;

      // Extract date fields and create prices object
      const prices: Record<string, number> = {};
      const nonDateFields: Record<string, unknown> = {};

      // Track which fields were actually updated in this operation
      const updatedFields = new Set<string>();

      Object.entries(rowData).forEach(([key, value]) => {
        const originalValue =
          originalData[getRowId(updatedRow)]?.[key as keyof OTCRow];

        // If this field changed from its original value, track it
        if (!areValuesEqual(value, originalValue)) {
          updatedFields.add(key);
        }

        if (DATE_FORMATS.YYYY_MM_DD_REGEX.test(key)) {
          // This is a date field
          if (value !== null && value !== '') {
            // Parse the value to handle commas properly
            const parsedValue = parseNumberWithCommas(String(value));
            if (!isNaN(parsedValue)) {
              // Only add to prices if value is not null, empty, or NaN
              prices[key] = parsedValue;
            }
          }
          // Skip adding null/empty date fields to prices object completely
        } else {
          // This is not a date field, add to nonDateFields regardless of value
          nonDateFields[key] = value;
        }
      });

      // Create the row for server with prices object
      const rowForServer = {
        ...nonDateFields,
        nav_prices: prices,
      };

      try {
        // Send the data with prices object to the server
        await Actions.updateOTC(
          rowForServer as OTCUpdateRequest,
          updatedRow.otc_id || updatedRow.id
        );

        // Create an updated version of the row that matches exactly what was sent to the server
        const updatedOriginalRow = {
          ...updatedRow,
          id: updatedRow.id, // Preserve the id
          [COLUMN_FIELDS.PNL]: updatedRow[COLUMN_FIELDS.PNL], // Preserve the PNL
        };

        // For date fields, only include those that were actually sent to the server
        Object.keys(updatedRow).forEach(key => {
          if (DATE_FORMATS.YYYY_MM_DD_REGEX.test(key)) {
            const value = updatedRow[key];
            // If value is null/empty, remove it from the original data
            if (value === null || value === '') {
              delete updatedOriginalRow[key];
            }
          }
        });

        // Update originalData with the sanitized row that matches server state
        setOriginalData(prevOriginal => ({
          ...prevOriginal,
          [getRowId(updatedRow)]: updatedOriginalRow,
        }));

        // After updating the server, check differences with Illio for this specific row
        if (updatedRow.otc_id) {
          try {
            await checkDifferenceWithIllioForRow(updatedRow, updatedFields);
          } catch (illioError) {
            // Silently fail - the update succeeded, just couldn't check Illio differences
            console.warn('Failed to check Illio differences:', illioError);
          }
        }
      } catch (error) {
        console.error('Error updating OTC:', error);
        showSnackbar({
          title: 'Update failed',
          type: SnackbarType.error,
          message: 'Failed to update OTC values',
        });
      }
    },
    [
      originalData,
      areValuesEqual,
      setOriginalData,
      checkDifferenceWithIllioForRow,
    ]
  );

  // Main row update processor
  const originalProcessRowUpdate = useCallback(
    (newRow: GridRowModel<OTCRow>) => {
      // Sanitize all string values to remove whitespace and hidden characters from copy/paste
      Object.keys(newRow).forEach(field => {
        if (field !== 'id' && typeof newRow[field] === 'string') {
          newRow[field] = sanitizeStringValue(newRow[field]);
        }
      });

      // Ensure currency is never empty, default to USD
      if (!newRow.currency || newRow.currency === '') {
        newRow.currency = 'USD';
      }

      // Create a local variable to track changes
      const rowChanges = new Set<string>();
      let hasChanges = false;

      const rowOriginal = originalData[getRowId(newRow)] || {};

      Object.keys(newRow).forEach(field => {
        if (
          field !== 'id' &&
          field !== COLUMN_FIELDS.PNL &&
          field !== DAYS_SINCE_ILLIO_UPDATE
        ) {
          // Skip checking PNL field
          const originalValue = rowOriginal[field as keyof OTCRow];
          const currentValue = newRow[field as keyof OTCRow];

          if (!areValuesEqual(currentValue, originalValue)) {
            rowChanges.add(field);
            hasChanges = true;
          }
        }
      });

      // Always recalculate P&L for any row change
      const dateFields = Object.keys(newRow)
        .filter(key => DATE_FORMATS.YYYY_MM_DD_REGEX.test(key))
        .sort((a, b) => b.localeCompare(a));

      // Recalculate P&L
      newRow[COLUMN_FIELDS.PNL] = calculatePnL(newRow, dateFields);

      // Update the data with the new row - SIMPLIFIED APPROACH
      setOTCData(prevData =>
        prevData.map(row => (row.id === newRow.id ? newRow : row))
      );

      // Only update server if row has actual changes
      if (hasChanges) {
        updateServerWithRow(newRow);
      }

      return newRow;
    },
    [
      originalData,
      areValuesEqual,
      calculatePnL,
      setOTCData,
      updateServerWithRow,
    ]
  );

  // Handle immediate cell edit to sanitize pasted values
  const handleCellEditCommit = useCallback(
    (params: GridCellEditCommitParams) => {
      const { id, field, value } = params;

      // Sanitize the value if it's a string
      const sanitizedValue =
        typeof value === 'string' ? sanitizeStringValue(value) : value;

      // If the value was changed by sanitization, update the data immediately
      if (sanitizedValue !== value) {
        setOTCData(prevData =>
          prevData.map(row =>
            row.id === id ? { ...row, [field]: sanitizedValue } : row
          )
        );
      }

      return sanitizedValue;
    },
    [setOTCData]
  );

  // Initialize undo/redo system
  const {
    wrappedProcessRowUpdate: processRowUpdate,
    undo,
    redo,
    canUndo,
    canRedo,
    getUndoActionInfo,
    getRedoActionInfo,
  } = useCellUndoRedo({
    processRowUpdate: originalProcessRowUpdate,
    getData: () => otcData,
  });

  return {
    processRowUpdate,
    handleCellEditCommit,
    undo,
    redo,
    canUndo,
    canRedo,
    getUndoActionInfo,
    getRedoActionInfo,
  };
};
